"use client";
import React, { useState } from "react";
import { Modal, ModalBody, ModalContent } from "@nextui-org/react";
import { useDictionary } from "../../context/dictionaryProvider";
import { ContactWrapShape, SendBtn } from "../molecules";
import InputTextField from "../atoms/InputTextField";
import PhoneField from "../atoms/PhoneField";
import { useForm } from "react-hook-form";
import { downloadBrochureAction } from "../../actions/brochure";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { bitrixSourceIds, getImageUrl } from "../../lib/util";
import { trackEnquiryTypeFormSubmit } from "../../lib/tracking";
import { bitrixLeadAction } from "../../actions/bitrix";
import { zohoLeadAction } from "../../actions/zoho";
import Loading from "../../app/[lang]/loading";
import Link from "next/link";
import ThankuModal from "./ThankuModal";
import { useSearchParams } from "next/navigation";
import { isPhoneValid } from "../../lib/validatePhone";
import { leadFields } from "../../emailTemplates/leadTemplate";
import { leadEmailAction } from "../../actions/email";
import EnquiryTypeToggle from "../atoms/EnquiryTypeToggle";
import { isCorporateEmail } from "../../lib/emailDomain";
import { trackMetaEvent } from "../../lib/metaTracking";
import { trackEvent } from "../../lib/engageXClient";
import { useFormTracking } from "../../lib/useFormTracking";


// ================================
// Schemas
// ================================
const formSchema = yup.object({
  name: yup
    .string()
    .required()
    .min(3, "Name must be at least 3 characters long")
    .max(25, "Name cannot exceed 25 characters")
    .matches(/^[a-zA-Z0-9\s]+$/, "No special character allowed"),
  email: yup
    .string()
    .required()
    .email()
    .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Email must have a valid domain")
    .test(
      "corporate-email",
      "Please enter a valid work email",
      function (value) {
        if (!this.parent?.isCorporate || !value) return true;
        return isCorporateEmail(value);
      }
    ),
  phone: yup
    .string()
    .required("Phone number is required")
    .test("is-valid-phone", "Phone number is not valid", (value) => !!value && isPhoneValid(value)),
  message: yup
    .string()
    .trim()
    .required()
    .matches(/^[a-zA-Z0-9\s]+$/, "No special character allowed"),
  isCorporate: yup.boolean().required(),
}).required();

// ================================
// Main Component
// ================================
const DownloadBrochureCourse = ({ document, course, isOpen, onOpenChange, location = "hero" }) => {
  const dict = useDictionary();
  const searchParams = useSearchParams();


  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(false);
  const [countryName, setCountryName] = useState("");
  const ft = useFormTracking({ formName: "brochure", open: isOpen, courseName: course?.course_name || course?.name, trigger: location });


  // ====== Main form hook ======
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      isCorporate: false,
    },
  });

  const isCorporate = watch("isCorporate", false);


  // ====== Submit Handlers ======
  const submitToBitrix = async (data, bitrixSourceId) => {
    const dataWithCountry = {
      ...data,
      country_name: countryName || ""
    };
    try {
      let res = await downloadBrochureAction({ ...data, course: course?.id });

      if (res?.error) {
        setResponse(res?.error?.message);
        return false;
      }

      setResponse("Thank you for your response");
      setSuccess(true);
      trackEvent("brochure_downloaded", {
        course: course?.course_name || course?.name,
        location,
        ...ft.submitted({ isCorporate: data?.isCorporate, country: countryName }),
      });
      trackMetaEvent({
        eventName: "Lead",
        email: data?.email,
        phone: data?.phone,
        customData: {
          content_name: course?.course_name || course?.name || course?.slug,
          content_category: "download_brochure",
          content_ids: course?.slug ? [course.slug] : undefined,
        },
      });

      const utm = {
        utm_source: localStorage.getItem("utm_source") || "",
        utm_medium: localStorage.getItem("utm_medium") || "",
        utm_campaign: localStorage.getItem("utm_campaign") || "",
        utm_term: localStorage.getItem("utm_term") || "",
        utm_content: localStorage.getItem("utm_content") || "",
        click_id: localStorage.getItem("click_id") || "",
      };

      void Promise.allSettled([
        bitrixLeadAction({ ...data, course }, searchParams, bitrixSourceId),
        zohoLeadAction(
          { ...dataWithCountry, ...utm, course },
          window.location.href,
          bitrixSourceId,
          "Download Brochure (Website)"
        ),
        leadEmailAction("Brochure", leadFields(data)),
      ]).then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") {
            console.error("Download brochure integration failed:", result.reason);
          }
        });
      });
      return true;
    } catch (err) {
      console.log(err);
      return false;
    } finally {
      reset({
        isCorporate: false,
      });
    }
  };


  const onSubmit = async (data) => {
    if (typeof trackEnquiryTypeFormSubmit === "function") {
      trackEnquiryTypeFormSubmit(
        "download_brochure_course",
        data?.isCorporate
      );
    }
    await submitToBitrix(data, bitrixSourceIds.downloadBoucher);
  };







  // Custom handler so modal only closes on explicit close button, not on outside click
  const handleModalOpenChange = (open) => {
    if (!open) {
      onOpenChange?.(false);
    }
  };


  return (
    <>
      {/* First Form Modal */}
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onOpenChange={handleModalOpenChange}
          backdrop="blur"
          size="xl"
          hideCloseButton
          isDismissable={false} // Prevent outside click close
        >
          <ModalContent>
            {!isSubmitting ? (
              <ModalBody>
                {/* custom close button */}
              <button
                type="button"
                aria-label="Close"
                className="absolute right-3 top-3 rounded-full p-2 hover:bg-gray-100"
                onClick={() => {
                  onOpenChange?.(false);
                }}
              >
                <span className="block text-2xl">&times;</span>
              </button>


                {!success ? (
                  <form ref={ft.ref} onSubmit={handleSubmit(onSubmit)} className="relative bg-[#fff] my-12 md:mx-10">
                    <div className="px-12 pb-28 pt-10 rounded-2xl border border-black">
                      <h3 className="text-2xl text-[#303030] mb-10 font-bold capitalize">{dict?.courses?.downloadBrochure}</h3>
                      <input type="hidden" {...register("isCorporate")} />
                      <EnquiryTypeToggle
                        isCorporate={isCorporate}
                        onChange={(value) =>
                          setValue("isCorporate", value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                      />
                      <InputTextField
                        label={dict?.form.fieldNames.name}
                        type="text"
                        isInvalid={!!errors?.name}
                        errorMessage={errors?.name?.message}
                        {...register("name")}
                      />
                      <InputTextField
                        label={
                          isCorporate
                            ? dict?.form?.fieldNames?.work_email || "Work Email"
                            : dict?.form.fieldNames.email
                        }
                        type="email"
                        isInvalid={!!errors?.email}
                        errorMessage={errors?.email?.message}
                        {...register("email")}
                      />
                      <PhoneField
                        label={dict?.form.fieldNames.phone}
                        name="phone"
                        value={getValues("phone")}
                        onChange={(value, metadata) => {
                          setValue("phone", value, { shouldDirty: true, shouldValidate: isDirty });
                          //  Capture country name
                          if (metadata?.country?.name) {
                            setCountryName(metadata.country.name);
                          }
                        }}
                        isInvalid={!!errors?.phone}
                        errorMessage={errors?.phone?.message}
                      />
                      <InputTextField
                        label={dict?.form.fieldNames.message}
                        type="text"
                        isInvalid={!!errors?.message}
                        errorMessage={errors?.message?.message}
                        {...register("message")}
                      />


                      <div className="flex items-center">
                        <span className="text-sm">{response}</span>
                        {success && document?.url && (
                          <>
                            {": "}
                            <Link prefetch={false} href={document?.url} target="_blank" className="ml-1 text-sm underline text-blue-600 hover:text-blue-700">
                              {dict?.viewDetail}
                            </Link>
                          </>
                        )}
                      </div>


                    </div>


                    <SendBtn
                      type="submit"
                      className="!p-4"
                      id="download-brochure"
                    >
                      {dict?.download}
                    </SendBtn>
                    <ContactWrapShape />
                  </form>
                ) : (
                  <ThankuModal
                    message="Thank you for downloading"
                    btnText="open"
                    handleClick={() => {
                      onOpenChange?.(false);
                      setResponse("");
                      reset();
                      setSuccess(false);
                      if (document?.url) window.open(getImageUrl(document), "_blank");
                    }}
                  />
                )}
              </ModalBody>
            ) : (
              <Loading />
            )}
          </ModalContent>
        </Modal>
      )}

    </>
  );
};

export default DownloadBrochureCourse;
